/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {login} from '../core/_requests'
import posthog from 'posthog-js'
import {useAuth} from '../core/Auth'
import {Helmet} from 'react-helmet'
import {auth1, googleProvider} from '../../../../services/firebase'
import {signInWithPopup} from 'firebase/auth'
import {ToastContainer} from 'react-toastify'
import {showSuccessToast} from 'app/components/ShowToast'
import Input from 'app/components/Input'
import Button from 'app/components/Button'
import { toAbsoluteUrl } from '_metronic/helpers'
import {handleAuthError} from 'app/modules/auth/helpers/authErrorHandler'
import {
  fetchUserDataFromFirestore,
  fetchPersonalDataFromFirestore,
  handleSocialAuth,
  triggerSidebarResize,
  createActivityLog,
  navigateUserByRole,
} from 'app/modules/auth/helpers/authHelpers'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .test('email-format', 'Please enter a complete email address', (value) => {
      if (!value) return false
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(value)
    }),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/
type LocationState = {
  accountCreated?: boolean
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  // const [githubLoading, setGithubLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const {accountCreated} = (location.state as LocationState) || {}
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (accountCreated) {
      showSuccessToast({message: 'Account created successfully'})
    }
  }, [accountCreated])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const userCredential = await login(values.email, values.password)
        const user = userCredential.user

        if (user.email) {
          posthog.identify(user.email, {
            email: user.email,
          })
        }
        const idTokenResult = await user.getIdTokenResult()
        if (idTokenResult) {
          const additionalUserData = await fetchUserDataFromFirestore(user.uid)

          const userPersonaData = await fetchPersonalDataFromFirestore(
          additionalUserData.tenantId,
          user.uid,
          additionalUserData.email
        )
        if (additionalUserData) {
          if (userPersonaData) {
            saveAuth({
              api_token: user.refreshToken,
              uid: user.uid,
              email: user.email,
              userName: user.displayName,
              password: values.password,
              first_name: additionalUserData.first_name,
              last_name: additionalUserData.last_name,
              tenantId: additionalUserData.tenantId || '',
              role: userPersonaData.role || '',
            })
            setCurrentUser({
              uid: user.uid,
              userName: additionalUserData.userName,
              api_token: user.refreshToken || '',
              password: values.password,
              email: additionalUserData.email || '',
              first_name: additionalUserData.first_name,
              last_name: additionalUserData.last_name,
              tenantId: additionalUserData.tenantId || '',
              role: userPersonaData.role || '',
              account_type: userPersonaData.account_type || '',
            })

            await createActivityLog(user, additionalUserData.tenantId, additionalUserData)
            
            navigateUserByRole(userPersonaData, navigate)
            
            triggerSidebarResize()
            } else {
              saveAuth(undefined)
              setStatus('Sorry, you have not been assign any role yet')
              setSubmitting(false)
              setLoading(false)
            }
          } else {
            saveAuth(undefined)
            setStatus('Sorry, you havent completed the registration process')
            setSubmitting(false)
            setLoading(false)
          }
        }
      } catch (error) {
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleGoogleSignIn = async () => {
    try {
      setError(null) 
      setGoogleLoading(true)
      const result = await signInWithPopup(auth1, googleProvider)
      const user = result.user
      if (user) {
        await handleSocialAuth(user, 'google', saveAuth, setCurrentUser, navigate)
      } else {
        console.error('Google authentication failed')
      }
    } catch (error: any) {
      setError(handleAuthError(error, 'Google'));
    } finally {
      setGoogleLoading(false)
    }
  }

  // const handleGithubSignIn = async () => {
  //   try {
  //     setError(null) 
  //     setGithubLoading(true)
  //     const result = await signInWithPopup(auth1, githubProvider)
  //     const user = result.user
  //     if (user) {
  //       await handleSocialAuth(user, 'github', saveAuth, setCurrentUser, navigate)
  //     } else {
  //       console.error('GitHub authentication failed')
  //     }
  //   } catch (error: any) {
  //     setError(handleAuthError(error, 'GitHub'));
  //   } finally {
  //     setGithubLoading(false)
  //   }
  // }

  return (
    <>
      <Helmet>
        <title>SecureSlate</title>
      </Helmet>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        <div className='text-center mb-11'>
          <div className='mb-4'>
        <a href='/' className='form-logo w-inline-block'>
            <img alt='Logo' src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')} className='h-40px' />
          </a>
          </div>
          <h1 className='text-dark fw-bolder mb-5 '>Login</h1>
          <div className='text-gray-700 fw-semibold fs-6'>
            Safeguarding Your Data with Robust Security Solutions.
          </div>
        </div>

        <div className='justify-content-center  row g-3 '>
        <div className='col-md-9'>
            <a
              className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100 mb-3'
              onClick={handleGoogleSignIn}
            >
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
                className='h-15px me-3'
              />
              {!googleLoading && <span className='indicator-label'>Sign in with Google</span>}
              {googleLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Signing in...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </a>
            {/* <p className='text-gray-600 fs-8 mt-1 text-center'>Sign in with google works for Trial accounts only</p> */}

            {/* <a
              className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100'
              onClick={handleGithubSignIn}
            >
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/svg/brand-logos/github-1.svg')}
                className='h-15px me-3'
              />
              {!githubLoading && <span className='indicator-label'>Sign in with GitHub</span>}
              {githubLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Signing in...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </a> */}
          </div>
        {/* end::Col */}

        {/* begin::Col */}
        {/* <div className='col-md-6'>
          
          <a
            href='#'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')}
              className='theme-light-show h-15px me-3'
            />
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black-dark.svg')}
              className='theme-dark-show h-15px me-3'
            />
            Sign in with Apple
          </a>
        </div> */}
        </div>
        {error && (
          <div className=' alert alert-danger mt-3'>
            <div className='alert-text font-weight-bold'>{error}</div>
          </div>
        )}
        {/* end::Login options */}

        {/* begin::Separator */}
        <div className='separator separator-content my-10'>
          <span className='w-125px text-gray-500 fw-semibold fs-7'>Or with email</span>
        </div>
        {/* end::Separator */}

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-primary-active'>
              Please enter your <strong>Email Address</strong> and <strong>Password</strong> to
              access your <strong>SecureSlate</strong> account
            </div>
          </div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <Input
            type='email'
            label='Email'
            placeholder='Enter your email'
            className={`bg-transparent ${
              formik.touched.email && !formik.errors.email && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
            {...formik.getFieldProps('email')}
          />
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <Input
            type='password'
            label='Password'
            placeholder='Enter your password'
            className={`bg-transparent ${
              formik.touched.password && !formik.errors.password && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            error={formik.touched.password && formik.errors.password ? formik.errors.password : ''}
            {...formik.getFieldProps('password')}
          />
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          <Link to='/auth/forgot-password' className='link-primary'>
            Forgot Password ?
          </Link>
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <Button
            type='submit'
            btnType='primary'
            id='kt_sign_in_submit'
            style={{height:'43.2px'}}
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Login</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        </div>
        {/* end::Action */}

        <div className='text-gray-700 text-center fw-semibold fs-6'>
          New to our platform?{' '}
          <Link to='/auth/registration' className='link-primary'>
          Lets get started
          </Link>
          {/* or
          <Link to='/auth/trial-registration' className='link-primary mx-2'>
            Explore a free trial
          </Link> */}
          {/* <a href='https://getsecureslate.com/pricing' className='link-primary'>
          Lets get started
        </a> */}
          {/* <button
          type='button'
          className='btn '
          style={{ margin: 0, padding: 0,color: '#31bd93' }}
          onClick={() => window.location.href = 'https://getsecureslate.com/pricing'}
        >
          <div className=' text-center fw-semibold fs-6 '>
          Lets get started
          </div>
          </button> */}
        </div>
      </form>
      <ToastContainer />
    </>
  )
}
